"use client"

import { useState } from "react"
import Link from "next/link"
import { Bell } from "lucide-react"
import { useAlertSubscribe } from "./use-alert-subscribe"
import { browserStorage, markInlineWatchUsed } from "./inline-watch-storage"

interface PriceWatchFormProps {
  productId: string
  productTitle: string
  /** §6(y) gate — computed server-side from the loaded direct offers.
   *  Eligible non-Amazon offers drive the existing "Watch Price" experience. */
  hasNonAmazonOffer: boolean
  /** Legacy prop name: a successful filtered direct-offer read returned no
   *  rows. This does not establish global stock; it enables the existing
   *  partner-availability subscription mode. */
  isOutOfStock?: boolean
  /** Retained for caller compatibility. This direct-offer minimum excludes
   *  Amazon and siblings and carries no freshness proof, so it is not shown. */
  bestNonAmazonPriceCents?: number | null
}

/**
 * Single on-page capture in the money zone. One collapsed orange button that
 * expands a compact inline form (first name + last name + email). Two honest
 * modes, never both, never a competing second widget:
 *
 *   • price mode  (hasNonAmazonOffer)  → "Watch Price" → type 'price_watch'
 *   • back mode   (isOutOfStock)       → partner availability →
 *     type 'back_in_stock'
 *
 * When neither holds (e.g. an Amazon-only in-stock product — §6(y)) the whole
 * component renders NOTHING (Real-or-Hidden).
 */
export function PriceWatchForm({
  productId,
  productTitle,
  hasNonAmazonOffer,
  isOutOfStock = false,
}: PriceWatchFormProps) {
  const mode: "price" | "back" | null = hasNonAmazonOffer
    ? "price"
    : isOutOfStock
      ? "back"
      : null

  const [expanded, setExpanded] = useState(false)
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [honeypot, setHoneypot] = useState("")
  const [optIn, setOptIn] = useState(false)
  const [thresholdDollars, setThresholdDollars] = useState("")
  const [frequency, setFrequency] = useState<"on_drop" | "daily" | "weekly">("on_drop")
  const { status, errorMessage, subscribe } = useAlertSubscribe()

  // Real-or-Hidden: no eligible mode → no surface at all.
  if (mode === null) return null

  const buttonLabel = mode === "price" ? "Watch Price" : "Email me when an offer is available"
  const submitLabel = mode === "price" ? "Watch price" : "Notify me"

  /**
   * One price intent, one ask. A successful inline subscribe marks the
   * suppression key the popup reads, so the visitor who just handed over an
   * email here is not asked for it again by the overlay (FRIDGE-279).
   */
  const onSubscribed = (ok: boolean) => {
    if (ok) markInlineWatchUsed(browserStorage())
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mode === "price") {
      const parsed = parseFloat(thresholdDollars)
      const thresholdCents =
        thresholdDollars.trim() !== "" && Number.isFinite(parsed) && parsed > 0
          ? Math.round(parsed * 100)
          : undefined
      void subscribe({
        type: "price_watch",
        email,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        honeypot,
        generalUpdatesOptIn: optIn,
        productId,
        thresholdCents,
        frequency,
      }).then(onSubscribed)
    } else {
      void subscribe({
        type: "back_in_stock",
        email,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        honeypot,
        generalUpdatesOptIn: optIn,
        productId,
      }).then(onSubscribed)
    }
  }

  if (status === "success") {
    return (
      <div
        className="mt-3 rounded-lg border border-[#ff9951]/45 bg-[#fff7ed] p-3 text-sm text-gray-700"
        data-testid="price-watch-form"
        data-state="success"
        data-mode={mode}
      >
        <p className="font-semibold text-gray-900">Almost there — check your inbox.</p>
        <p className="text-xs text-gray-600 mt-1">
          {mode === "price"
            ? "We just emailed you a confirmation link. Click it to start your price watch for eligible non-Amazon offers — nothing arrives, and no alerts start, until you confirm."
            : "We just emailed you a confirmation link. Click it to start your availability alert for eligible non-Amazon offers — nothing arrives until you confirm."}
        </p>
      </div>
    )
  }

  // Collapsed: the affordance has to earn the click on its own (FRIDGE-279).
  //
  // It used to be a bare 32px button on a hairline rule, labelled with the
  // feature ("Watch Price") and explained in 11px grey underneath — the weakest
  // element in the money zone, asking for an email while giving no reason to
  // hand one over. It now reads as a card: a light orange wash inside the
  // brand's light-background rule, a benefit-first headline, ONE true sentence
  // of value, and a full-width tap target on mobile.
  //
  // What it deliberately does NOT do: no countdown, no fake scarcity, no
  // pre-ticked opt-in, no interstitial, and no claim about how often the price
  // is checked. Per-product snapshot cadence is roughly every 10–14 days, not
  // daily, so "we check every day" would have been an invented number. The
  // affiliate CTA above stays the primary action — this card is a wash and a
  // button, not a second solid-filled competitor for the same glance.
  if (!expanded) {
    return (
      <div
        className="mt-3 rounded-lg border border-[#ff9951]/45 bg-[#fff7ed] p-3"
        data-testid="price-watch-form"
        data-state="collapsed"
        data-mode={mode}
      >
        <div className="flex items-start gap-2.5">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-[#ff9951]/40"
            aria-hidden="true"
          >
            <Bell className="h-4 w-4 text-[#ff8533]" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold leading-snug text-gray-900">
              {mode === "price" ? "Know when this price drops" : "Know when a partner offer is available"}
            </p>
            <p className="mt-0.5 text-xs leading-snug text-gray-700">
              {mode === "price"
                ? "Get an email when the price drops at an eligible non-Amazon retailer."
                : "Get an email when an eligible non-Amazon partner offer becomes available."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#ff9951] px-4 py-2.5 text-sm font-bold text-[var(--color-brand-action-text)] transition-colors hover:bg-[#ff8533] sm:w-auto"
          aria-expanded={false}
          data-testid="price-watch-toggle"
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {buttonLabel}
        </button>
        <p className="mt-2 text-[11px] text-gray-600">
          Free · unsubscribe anytime
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 rounded-lg border border-[#ff9951]/45 bg-[#fff7ed] p-3"
      data-testid="price-watch-form"
      data-state="expanded"
      data-mode={mode}
    >
      <p className="text-xs font-bold text-gray-900 uppercase tracking-wide">
        {mode === "price" ? "Price Watch" : "Partner availability alert"}
      </p>
      <p className="text-xs text-gray-600 mt-0.5">
        {mode === "price"
          ? `Get price-drop emails for eligible non-Amazon offers on ${productTitle}.`
          : `Get an email when an eligible non-Amazon partner offer for ${productTitle} becomes available.`}
      </p>
      {/* First + last name — optional, side by side. */}
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <input
          name="first_name"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name (optional)"
          autoComplete="given-name"
          maxLength={60}
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="First name (optional)"
          data-testid="pw-first-name"
        />
        <input
          name="last_name"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last name (optional)"
          autoComplete="family-name"
          maxLength={60}
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="Last name (optional)"
          data-testid="pw-last-name"
        />
      </div>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <input
          name="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
          aria-label="Email address for alerts"
          data-testid="pw-email"
        />
        <button
          type="submit"
          disabled={status === "submitting" || !email.trim()}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wide bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] rounded-md transition-colors disabled:opacity-100 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? "Saving…" : submitLabel}
        </button>
      </div>
      {mode === "price" && (
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            Alert me below
            <span aria-hidden="true">$</span>
            <input
              name="threshold"
              type="number"
              min="1"
              step="0.01"
              value={thresholdDollars}
              onChange={(e) => setThresholdDollars(e.target.value)}
              placeholder="any drop"
              className="w-24 px-2 py-1 text-xs rounded-md border border-gray-300 focus:border-[#ff9951] outline-none"
              aria-label="Optional price threshold in dollars"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            Frequency
            <select
              name="frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as "on_drop" | "daily" | "weekly")}
              className="px-2 py-1 text-xs rounded-md border border-gray-300 focus:border-[#ff9951] outline-none bg-white"
              aria-label="Alert frequency"
            >
              <option value="on_drop">When it drops</option>
              <option value="daily">Daily summary</option>
              <option value="weekly">Weekly summary</option>
            </select>
          </label>
        </div>
      )}
      {/* Honeypot — hidden from real users; bots that fill it are rejected. */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />
      <label className="mt-2 flex items-start gap-2 text-xs text-gray-600">
        <input
          name="general_updates"
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
          className="mt-0.5"
          data-testid="general-updates-optin"
        />
        Also send me occasional Fridge.com updates
      </label>
      {status === "error" && errorMessage && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
      <p className="mt-2 text-[11px] text-gray-500">
        We may earn an affiliate commission when you buy through our links. This never affects
        our reviews or comparisons.{" "}
        <Link href="/privacy-policy" className="underline hover:text-gray-700">
          Privacy policy
        </Link>
      </p>
    </form>
  )
}

export default PriceWatchForm
